import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { cn } from '@mlm/ui/lib/cn';
import { canScheduleLocalNotifications } from '@mlm/ui/lib/native-shell';
import {
  CoachButton,
  ErrorNote,
  Eyebrow,
  Field,
  inputClass,
  Loading,
  Screen,
} from '../components/primitives';
import { coaching, type CoachingProfile } from '../lib/api';
import { qk, useProfile } from '../lib/queries';
import { syncReminders } from '../lib/reminders';

/**
 * SC-42: reminder times and coach nudges. The phone app delivers the two
 * reminders itself, on the device; on the web the choices are saved to the
 * account and take effect once the member signs in on the app.
 */
export function NotificationSettingsPage() {
  const profile = useProfile();
  if (profile.isPending) return <Loading />;
  if (profile.isError)
    return <ErrorNote error={profile.error} retry={() => void profile.refetch()} />;
  return <NotificationForm profile={profile.data.profile} />;
}

function NotificationForm({ profile }: { profile: CoachingProfile }) {
  const qc = useQueryClient();
  const prefs = profile.notificationPrefs ?? {};
  const [morningOn, setMorningOn] = useState(Boolean(prefs.morningReminder));
  const [morning, setMorning] = useState(prefs.morningReminder ?? '06:30');
  const [eveningOn, setEveningOn] = useState(Boolean(prefs.eveningReminder));
  const [evening, setEvening] = useState(prefs.eveningReminder ?? '21:00');
  const [nudges, setNudges] = useState(prefs.coachNudges ?? true);
  const [denied, setDenied] = useState(false);
  const incomplete = (morningOn && !morning) || (eveningOn && !evening);
  const onPhone = canScheduleLocalNotifications();

  const save = useMutation({
    mutationFn: async () => {
      const notificationPrefs = {
        morningReminder: morningOn ? morning : null,
        eveningReminder: eveningOn ? evening : null,
        coachNudges: nudges,
      };
      const saved = await coaching.updateProfile({ notificationPrefs });
      // Saving is the moment the member asked for reminders, so this is the one
      // place allowed to show Android's permission dialog.
      const result = await syncReminders(notificationPrefs, saved.profile.timezone, {
        prompt: true,
      });
      return { saved, result };
    },
    meta: { successMessage: 'Reminder settings saved.' },
    onSuccess: ({ result }) => {
      setDenied(result === 'denied');
      void qc.invalidateQueries({ queryKey: qk.profile });
    },
  });

  return (
    <Screen
      back="/settings"
      eyebrow="Settings"
      title="Notifications"
      subtitle="Choose when to be reminded to begin and close your day."
    >
      <section className="space-y-6 border-b border-border px-5 py-6">
        <Eyebrow>Reminders</Eyebrow>
        <Reminder
          id="morning"
          label="Morning ritual reminder"
          on={morningOn}
          onToggle={setMorningOn}
          time={morning}
          onTime={setMorning}
        />
        <Reminder
          id="evening"
          label="Evening review reminder"
          on={eveningOn}
          onToggle={setEveningOn}
          time={evening}
          onTime={setEvening}
        />
        <label className="flex items-start gap-3 text-[0.95rem] text-ink">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 accent-verde"
            checked={nudges}
            onChange={(e) => setNudges(e.target.checked)}
          />
          <span>
            Coach nudges
            <span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground">
              A short note from Master Kala when a goal needs attention. Nudges begin in a later
              update; your choice is saved now.
            </span>
          </span>
        </label>
        <p className="text-xs leading-relaxed text-muted-foreground">
          {onPhone
            ? 'Reminders arrive on this phone at the times above, in your coaching timezone.'
            : 'Reminders arrive in the Miracles Protocol phone app. Your choices are saved to your account.'}
        </p>
        {denied && (
          <p role="alert" className="text-xs leading-relaxed text-destructive">
            Notifications are turned off for Miracles Protocol. Allow them in your phone's settings,
            then save again.
          </p>
        )}
        <CoachButton disabled={incomplete || save.isPending} onClick={() => save.mutate()}>
          Save reminders
        </CoachButton>
      </section>
    </Screen>
  );
}

function Reminder({
  id,
  label,
  on,
  onToggle,
  time,
  onTime,
}: {
  id: string;
  label: string;
  on: boolean;
  onToggle: (on: boolean) => void;
  time: string;
  onTime: (time: string) => void;
}) {
  return (
    <div className="space-y-3">
      <label className="flex min-h-11 items-center gap-3 text-[0.95rem] text-ink">
        <input
          type="checkbox"
          className="h-4 w-4 accent-verde"
          checked={on}
          onChange={(e) => onToggle(e.target.checked)}
        />
        {label}
      </label>
      {on && (
        <Field label="Time" htmlFor={`reminder-${id}`}>
          <input
            id={`reminder-${id}`}
            type="time"
            required
            className={cn(inputClass, 'max-w-[11rem] [color-scheme:dark]')}
            value={time}
            onChange={(e) => onTime(e.target.value)}
          />
        </Field>
      )}
    </div>
  );
}
